import { z } from 'zod'
import { api, createValidatedRequest } from './baseApi'

const DataStatusSchema = z.enum(['ok', 'noFunding', 'mixedCurrencies', 'unavailable'])

const CapitalSummarySchema = z.object({
  status: DataStatusSchema,
  currency: z.string().nullable(),
  amountFunded: z.number(),
  creditLine: z.number(),
  redeemed: z.number(),
  remaining: z.number(),
  redeemedPercent: z.number(),
  firstFundingDate: z.string().nullable(),
  latestFundingDate: z.string().nullable(),
  fundingCount: z.number(),
  monthlyRedemptionPace: z.number(),
  projectedFullyRedeemedMonth: z.string().nullable(),
})

/** Each total is null when the Hub hides that metric for the merchant. */
const ActivityTotalsSchema = z.object({
  sales: z.number().nullable(),
  orders: z.number().nullable(),
  members: z.number().nullable(),
  averageBill: z.number().nullable(),
  tips: z.number().nullable(),
  guests: z.number().nullable(),
  perksUsed: z.number().nullable(),
  cashbackEarned: z.number().nullable(),
})

const ActivityBucketSchema = z.object({
  start: z.string(),
  sales: z.number().nullable(),
  orders: z.number().nullable(),
  members: z.number().nullable(),
})

const ActivitySchema = z.object({
  status: DataStatusSchema,
  currency: z.string().nullable(),
  from: z.string(),
  to: z.string(),
  /** The previous-period fields are null when the Hub hides the period comparison. */
  previousFrom: z.string().nullable(),
  previousTo: z.string().nullable(),
  granularity: z.enum(['daily', 'weekly', 'monthly']),
  current: ActivityTotalsSchema,
  previous: ActivityTotalsSchema.nullable(),
  series: z.array(ActivityBucketSchema),
  previousSeries: z.array(ActivityBucketSchema).nullable(),
  peakTimes: z.array(z.object({ dayOfWeek: z.number(), hour: z.number(), orders: z.number() })),
  locations: z.array(
    z.object({
      locationId: z.string(),
      name: z.string(),
      sales: z.number().nullable(),
      orders: z.number().nullable(),
      members: z.number().nullable(),
      averageBill: z.number().nullable(),
    })
  ),
})

const MemberMixSchema = z.object({
  from: z.string(),
  to: z.string(),
  newMembers: z.number(),
  returningMembers: z.number(),
  repeatVisitors: z.number(),
})

const FundingHistoryMonthSchema = z.object({
  year: z.number(),
  month: z.number(),
  currency: z.string(),
  amount: z.number(),
  fundingCount: z.number(),
})

const FundingHistorySchema = z.object({
  status: DataStatusSchema,
  months: z.array(FundingHistoryMonthSchema),
})

const CreateFundingRequestResponseSchema = z.object({ id: z.string() })

const FundingRequestSummarySchema = z.object({
  id: z.string(),
  createdOn: z.string(),
  requestedAmount: z.number(),
  currency: z.string(),
  reason: z.string(),
  locationIds: z.array(z.string()),
  requestedByName: z.string(),
})

const FundingRequestSummariesSchema = z.array(FundingRequestSummarySchema)

export const GUEST_FLOW_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

const GuestFlowWindowSchema = z.object({
  day: z.enum(GUEST_FLOW_DAYS),
  startMinute: z.number(),
  endMinute: z.number(),
  intent: z.enum(['boost', 'hold']),
})

const GuestFlowTimeRangeSchema = GuestFlowWindowSchema.omit({ day: true })

const GuestFlowDateOverrideSchema = z.object({
  date: z.string(),
  windows: z.array(GuestFlowTimeRangeSchema),
  label: z.string().nullish(),
})

const TimeRangeSchema = z.object({ start: z.string(), end: z.string() })

const BusinessScheduleSchema = z.object({
  schedule: z.record(z.string(), z.object({ timeRanges: z.array(TimeRangeSchema) })),
  temporarilyClosed: z.boolean().nullish(),
})

const GuestFlowPlanSchema = z.object({
  windows: z.array(GuestFlowWindowSchema),
  note: z.string().nullable(),
  dateOverrides: z.array(GuestFlowDateOverrideSchema),
  extraCovers: z.number().nullable(),
  groupsWelcome: z.boolean(),
  updatedOn: z.string(),
  updatedByName: z.string().nullable(),
})

const GuestFlowLocationSchema = z.object({
  merchantId: z.string(),
  merchantName: z.string().nullable(),
  locationId: z.string(),
  locationName: z.string(),
  locationStatus: z.string(),
  timeZone: z.string().nullable(),
  businessSchedule: BusinessScheduleSchema.nullable(),
  source: z.enum(['none', 'merchant', 'location']),
  plan: GuestFlowPlanSchema.nullable(),
})

const GuestFlowOverviewSchema = z.object({
  merchantPlan: GuestFlowPlanSchema.nullable(),
  canEditMerchantPlan: z.boolean(),
  locations: z.array(GuestFlowLocationSchema),
})

/** Dashboard pages, Overview metrics and reports the Hub hid for the merchant; unknown keys are ignored. */
const DashboardVisibilitySchema = z.object({
  hiddenPages: z.array(z.string()),
  hiddenMetrics: z.array(z.string()),
  hiddenReports: z.array(z.string()).default([]),
})

export type DashboardVisibility = z.infer<typeof DashboardVisibilitySchema>
export type CapitalSummary = z.infer<typeof CapitalSummarySchema>
export type Activity = z.infer<typeof ActivitySchema>
export type ActivityTotals = z.infer<typeof ActivityTotalsSchema>
export type ActivityBucket = z.infer<typeof ActivityBucketSchema>
export type MemberMix = z.infer<typeof MemberMixSchema>
export type FundingHistory = z.infer<typeof FundingHistorySchema>
export type FundingHistoryMonth = z.infer<typeof FundingHistoryMonthSchema>
export type CreateFundingRequestResponse = z.infer<typeof CreateFundingRequestResponseSchema>
/** A past funding request. The review status is internal to Mezza and not returned. */
export type FundingRequestSummary = z.infer<typeof FundingRequestSummarySchema>

export type GuestFlowDay = (typeof GUEST_FLOW_DAYS)[number]
/** A recurring weekly window in the location's local time; minutes from midnight, end exclusive (1440 = midnight). */
export type GuestFlowWindow = z.infer<typeof GuestFlowWindowSchema>
export type GuestFlowIntent = GuestFlowWindow['intent']
export type GuestFlowBusinessSchedule = z.infer<typeof BusinessScheduleSchema>
export type GuestFlowTimeRange = z.infer<typeof GuestFlowTimeRangeSchema>
/** A special date whose windows replace the weekly pattern that day. */
export type GuestFlowDateOverride = z.infer<typeof GuestFlowDateOverrideSchema>
/** A saved plan: for all locations, or a location's own. */
export type GuestFlowPlan = z.infer<typeof GuestFlowPlanSchema>
/** A location with the plan it follows; `source` says whether that is its own, the all-locations one, or none. */
export type GuestFlowLocation = z.infer<typeof GuestFlowLocationSchema>
export type GuestFlowOverview = z.infer<typeof GuestFlowOverviewSchema>

export interface SaveGuestFlowBody {
  windows: GuestFlowWindow[]
  note: string | null
  dateOverrides: GuestFlowDateOverride[]
  extraCovers: number | null
  groupsWelcome: boolean
}

/** A request for additional funding; `requestedAmount` is in minor units (cents). */
export interface CreateFundingRequestBody {
  requestedAmount: number
  reason: string
  locationIds: string[]
}

/** Inclusive local dates in `yyyy-MM-dd`, the format the API expects. */
export interface DashboardRange {
  from: string
  to: string
}

export const getDashboardVisibility = async () =>
  createValidatedRequest(
    DashboardVisibilitySchema,
    api.get<DashboardVisibility>('/merchant/dashboard/visibility')
  )

export const getDashboardCapital = async () =>
  createValidatedRequest(CapitalSummarySchema, api.get<CapitalSummary>('/merchant/dashboard/capital'))

export const getDashboardActivity = async ({ from, to }: DashboardRange) =>
  createValidatedRequest(
    ActivitySchema,
    api.get<Activity>('/merchant/dashboard/activity', { params: { from, to } })
  )

export const getDashboardMemberMix = async ({ from, to }: DashboardRange) =>
  createValidatedRequest(
    MemberMixSchema,
    api.get<MemberMix>('/merchant/dashboard/members', { params: { from, to }, timeout: 60000 })
  )

export const getDashboardFundingHistory = async () =>
  createValidatedRequest(FundingHistorySchema, api.get<FundingHistory>('/merchant/dashboard/funding/history'))

export const createFundingRequest = async (body: CreateFundingRequestBody) =>
  createValidatedRequest(
    CreateFundingRequestResponseSchema,
    api.post<CreateFundingRequestResponse>('/merchant/dashboard/funding/requests', body)
  )

export const getFundingRequests = async () =>
  createValidatedRequest(
    FundingRequestSummariesSchema,
    api.get<FundingRequestSummary[]>('/merchant/dashboard/funding/requests')
  )

export const getGuestFlow = async () =>
  createValidatedRequest(GuestFlowOverviewSchema, api.get<GuestFlowOverview>('/merchant/dashboard/guest-flow'))

/** Replaces the all-locations plan. */
export const saveMerchantGuestFlow = async (body: SaveGuestFlowBody) =>
  createValidatedRequest(GuestFlowOverviewSchema, api.put<GuestFlowOverview>('/merchant/dashboard/guest-flow/merchant', body))

/** Gives each location its own copy of the plan. */
export const saveLocationsGuestFlow = async (locationIds: string[], plan: SaveGuestFlowBody) =>
  createValidatedRequest(
    GuestFlowOverviewSchema,
    api.put<GuestFlowOverview>('/merchant/dashboard/guest-flow/locations', { locationIds, plan })
  )

/** Removes the locations' own plans so they follow the all-locations plan again. */
export const resetLocationsGuestFlow = async (locationIds: string[]) =>
  createValidatedRequest(
    GuestFlowOverviewSchema,
    api.post<GuestFlowOverview>('/merchant/dashboard/guest-flow/locations/reset', { locationIds })
  )
